import { RARITY_IDS, Rarity } from "@highmarch/domain";
import { ChestStage } from "../render/chestStage";
import { audio } from "../audio/audio";
import { btnHtml, bump, motionOk } from "./kit";
import { ScreenFlash } from "./screenFlash";
import { uiUrl } from "./portraits";
import { burstPlan, chargeIdleSeconds, chargeRays, rattlePitch, tierUpPitch } from "./revealPlan";
import { appHeight } from "./appSize";
import { cardFocus, dealCards, type CardDeal } from "./chestCards";
import { payout, type PayoutAmounts } from "./chestPayout";

/**
 * The chest slot machine (art wow pass Phase 2, chests v8 wow pass 2026-10-12): on OPEN the 3D chest lifts off the end
 * card and flies to a full-screen stage in the vault (a reward chest drops in instead).
 *
 *   1. Climb — the chest arrives as a plain common one and nobody knows what it is. Each TAP rattles it; while it is
 *      below its real rarity the tap also cracks it into the next tier on its way (revealPlan.climbPath: a flash, the
 *      tier's colour on the rays, sigil, scrim and ribbon, "RARE!"). The tap that finds it already at the top opens it.
 *      There are no pips: how many taps a chest takes would give it away. An untapped chest is tapped for the player.
 *   2. Burst — the lock snaps, the lid is thrown in slow motion with the camera pushing in and turning, the rank-scaled
 *      burst and flash; a legendary rains gold feathers and coins, a mythic arcs lightning between its crystals.
 *   3. Cards — out of the mouth face-down, then over one at a time, worst first; epic and better held glowing first
 *      (chestCards.ts).
 *   4. COLLECT — the loot card's coins fly into wallet chips that count up (chestPayout.ts), then the stage fades.
 *
 * A tap during 2–3 finishes everything at once. Sounds and haptics are the audio port's chest cues.
 */
export interface SlotOptions {
  /** The overlay goes here (the end screen: a full-screen stacking context). */
  host: HTMLElement;
  rarity: Rarity;
  stage: ChestStage;
  /** The reward cards (gear first, loot card last), already built. */
  cards: HTMLElement[];
  /** What the loot card pays, counted into the wallet chips at COLLECT. */
  payout: PayoutAmounts;
  /** COLLECT tapped, after the payout and the overlay's fade. */
  onCollect: () => void;
}

/** The stage's height as a share of the screen (ui.md F2: ≈ 0.55·appHeight()). */
const STAGE_H = 0.55;
/** From a tap to the next tier cracking out of the chest (ms): the rattle first, then the pop. */
const TIER_UP_MS = 220;
/** The cards wait this long after the burst (ms), and the DOM flash stays under this over the 3D burst's own light. */
const CARDS_AFTER_BURST_MS = 380;
const SCREEN_PEAK_MAX = 0.26;
/** The latest the chest takes taps after the stage opens (ms), landed or not. */
const LAND_BY_MS = 1500;
const STINGER = { 4: "rarityLegendary", 5: "rarityMythic" } as const;
const rankOf = (id: string): number => Math.max(0, (RARITY_IDS as readonly string[]).indexOf(id));

export function chestSlot(o: SlotOptions): void {
  const rid = RARITY_IDS[o.rarity];
  const rank = rankOf(rid);
  const el = document.createElement("div");
  el.className = `rstage ${o.stage.showing}`;
  el.innerHTML = `
    <div class="rs-scrim"><i class="rs-vault" style="background-image: url('${uiUrl("chests/chest_vault.webp")}')"></i><i class="rs-tint"></i></div>
    <div class="rs-canvas"></div>
    <div class="ribbon disp rs-title veiled">CHEST</div>
    <div class="rs-well"></div>
    <div class="rs-prompt disp"><span class="rs-tap">TAP!</span></div>
    <div class="rs-cards"></div>
    ${btnHtml("COLLECT", "gold", { className: "rs-collect disp accent hidden" })}
    <small class="rs-hint hidden">tap to skip</small>`;
  // where the chest sits on the end card, before its canvas leaves it: the flight starts there
  const card = o.stage.canvas.isConnected ? o.stage.canvas.getBoundingClientRect() : null;
  o.host.append(el);
  const well = el.querySelector<HTMLElement>(".rs-well")!;
  const prompt = el.querySelector<HTMLElement>(".rs-prompt")!;
  const tapWord = el.querySelector<HTMLElement>(".rs-tap")!;
  const cardsBox = el.querySelector<HTMLElement>(".rs-cards")!;
  const collect = el.querySelector<HTMLButtonElement>(".rs-collect")!;
  const hint = el.querySelector<HTMLElement>(".rs-hint")!;
  const title = el.querySelector<HTMLElement>(".rs-title")!;

  // ---- the stage: the whole screen, the chest framed in the well's band (the top 55 % under the ribbon) ----
  const w = Math.round(innerWidth), full = Math.round(appHeight()), h = Math.round(full * STAGE_H);
  well.style.height = `${h}px`;
  const layer = el.querySelector<HTMLElement>(".rs-canvas")!;
  const host = el.getBoundingClientRect();
  const wellTop = well.getBoundingClientRect().top - host.top;
  o.stage.resize(w, full, Math.min(2, ChestStage.pixelRatio()), wellTop + h * 0.5, ChestStage.StageFill);
  layer.append(o.stage.canvas);

  const timers: number[] = [];
  const later = (ms: number, fn: () => void) => timers.push(window.setTimeout(() => { if (el.isConnected) fn(); }, ms));
  const motion = motionOk() && typeof el.animate === "function";
  const pop = (text: string, cls: string) => {
    const p = document.createElement("b");
    p.className = `rs-tier disp ${cls}`;
    p.textContent = text;
    el.append(p);
    if (!motion) { later(700, () => p.remove()); return; }
    p.animate([{ transform: "translate(-50%, 10px) scale(.3)", opacity: 0 }, { transform: "translate(-50%, -6px) scale(1.25)", opacity: 1, offset: 0.25 },
      { transform: "translate(-50%, -14px) scale(1)", opacity: 1, offset: 0.7 }, { transform: "translate(-50%, -40px) scale(.95)", opacity: 0 }],
    { duration: 900, easing: "ease-out", fill: "both" }).onfinish = () => p.remove();
  };
  /** The scrim, the ribbon and the rays wear the tier the chest shows now. */
  const wear = (tier: string, named: boolean) => {
    el.className = `rstage ${tier}`;
    if (!named) return;
    title.classList.remove("veiled");
    title.textContent = `${tier.toUpperCase()} CHEST`;
    bump(title, 1.15, 220);
  };

  // ---- 1. the climb ----
  let phase: "enter" | "charge" | "open" | "cards" | "done" | "pay" = "enter";
  let tapsDone = 0, busy = false, idle = 0;
  const armIdle = () => {
    window.clearTimeout(idle);
    idle = window.setTimeout(() => { if (el.isConnected && phase === "charge") tap(); }, chargeIdleSeconds(rankOf(o.stage.showing)) * 1000);
  };
  const tap = () => {
    if (busy) return;
    window.clearTimeout(idle);
    const climbing = !o.stage.atTop;
    o.stage.rattle(chargeRays(tapsDone + 1, o.stage.path.length));
    audio.play("chestRattle", 0, rattlePitch(tapsDone));
    tapsDone++;
    bump(prompt, 1.15, 180);
    if (!climbing) { openChest(); return; }
    busy = true;
    later(TIER_UP_MS, () => {
      busy = false;
      const tier = o.stage.upgrade();
      if (!tier) { openChest(); return; }
      const r = rankOf(tier);
      audio.play("chestTierUp", 0, tierUpPitch(r));
      wear(tier, true);
      pop(`${tier.toUpperCase()}!`, tier);
      tapWord.textContent = "AGAIN!";
      armIdle();
    });
  };

  // ---- 2. the burst, 3. the cards ----
  let deal: CardDeal | null = null;
  const openChest = () => {
    phase = "open";
    window.clearTimeout(idle);
    prompt.classList.add("gone");
    hint.classList.remove("hidden");
    o.stage.onLock = () => audio.play("chestLockBreak");
    o.stage.onArc = () => audio.play("mythicArc", 0, 0.9 + Math.random() * 0.25);
    o.stage.onBurst = () => {
      const plan = burstPlan(rank);
      revealRarity();
      audio.play("chestBurst");
      audio.play("chestCoins", 0.15);
      const sting = STINGER[rank as 4 | 5];
      if (sting) audio.play(sting, 0.3);
      ScreenFlash.flash("#fff8e6", Math.min(SCREEN_PEAK_MAX, plan.screenPeak), 120 + 20 * rank);
      later(motion ? CARDS_AFTER_BURST_MS : 0, launchCards);   // the burst reads first, then the cards leave the mouth
    };
    o.stage.open(() => { if (!deal) launchCards(); });
  };

  const revealRarity = () => {
    if (title.dataset.shown) return;
    title.dataset.shown = "1";
    wear(rid, true);
  };

  const launchCards = () => {
    if (deal) return;
    revealRarity();
    phase = "cards";
    const cr = o.stage.canvas.getBoundingClientRect();
    const m = o.stage.mouthOnCanvas();
    deal = dealCards(cardsBox, o.cards, { x: cr.left + m.x, y: cr.top + m.y }, motion, done);
  };

  // ---- 4. collect ----
  function done(): void {
    if (phase === "done" || phase === "pay") return;
    phase = "done";
    deal?.finish();
    hint.classList.add("hidden");
    collect.classList.remove("hidden");
    if (motion) collect.animate([{ transform: "translateY(30px) scale(.9)", opacity: 0 }, { transform: "none", opacity: 1 }], { duration: 280, easing: "cubic-bezier(.2,1.3,.4,1)" });
  }
  collect.onclick = (e) => {
    e.stopPropagation();
    if (phase !== "done") return;
    phase = "pay";
    collect.disabled = true;
    collect.classList.add("hidden");
    audio.play("chestWhoosh", 0, 1.2);
    const loot = o.cards.find((c) => c.classList.contains("loot")) ?? null;
    payout(el, loot, o.payout, motion, () => {
      for (const t of timers) window.clearTimeout(t);
      const leave = () => { el.remove(); o.onCollect(); };        // removing the canvas disposes the stage (its own isConnected check)
      if (!motion) { leave(); return; }
      el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 260, easing: "ease-in", fill: "forwards" }).onfinish = leave;
    });
  };

  el.addEventListener("pointerdown", (e) => {
    if ((e.target as HTMLElement).closest(".rs-collect")) return;
    if (phase === "done") {
      // face up: a tap on a gear card shows it in focus (the shops' item view)
      const face = (e.target as HTMLElement).closest(".rs-slot")?.querySelector<HTMLElement>(".itemcard");
      const focus = face ? cardFocus.get(face) : undefined;
      if (focus) { e.stopPropagation(); focus(); }
      return;
    }
    if (phase === "charge") tap();
    else if (phase === "open") { o.stage.skip(); launchCards(); done(); }
    else if (phase === "cards") done();
  });

  if (motion) el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: "ease-out" });
  const landed = () => {
    if (phase !== "enter") return;
    audio.play("chestLand");
    bump(title, 1.08, 200);
    phase = "charge";
    armIdle();
  };
  if (card && card.width > 0) {
    // off the end card: from where it sat there (canvas px of the full-screen stage), at the share of the height it filled
    audio.play("chestWhoosh");
    o.stage.flyIn({ x: card.left + card.width / 2 - host.left, y: card.top + card.height / 2 - host.top, fill: (ChestStage.CardFill * card.height) / full }, landed);
  } else o.stage.enter(landed);
  later(LAND_BY_MS, landed);   // whatever happens to the entrance, the chest takes taps by then
}
